import { guardedFetch } from './liveWrites.js';

/* Email notices that did not go out (S04b).
 *
 * The API (api/src/functions/outbox.js) lists the notices of the desks you work that
 * failed, were given up, or are "stuck", and lets you press Retry. It is dark until
 * the server switch IT_HELP_ATOMIC_SAVE is on: until then it answers 404 and this
 * panel simply does not appear. 403 (not staff for any desk) hides it too. The API is
 * the gate; hiding here is UX only.
 */

const CSRF = { 'x-requested-by': 'ihs-it-help' };

/** The API sends UTC wall-clock text like "2026-10-19 18:38:27.408". Read it as UTC. */
export function parseUtc(text) {
  if (!text) return null;
  const s = String(text);
  const d = new Date(s.replace(' ', 'T') + (/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? '' : 'Z'));
  return Number.isNaN(d.getTime()) ? null : d;
}

export const STATUS_LABEL = {
  failed: 'Failed - will try again',
  'gave-up': 'Gave up - needs you',
  stuck: 'Stuck - never sent',
};

/** One short sentence: what to do about it. */
export function nextStep(item) {
  if (item.status === 'gave-up') return 'Press Retry to send it again.';
  if (item.status === 'stuck') return 'The server stopped before sending it. Press Retry.';
  return 'Press Retry to send it now.';
}

/** What a Retry answer means, in plain words. */
export function describeRetry(result) {
  switch (result) {
    case 'sent': return { tone: 'good', text: 'Sent.' };
    case 'already-sent': return { tone: 'good', text: 'It was already sent. Nothing more was sent.' };
    case 'busy': return { tone: 'flat', text: 'Another send is working on it right now. Check again in a minute.' };
    case 'lost-lease': return { tone: 'flat', text: 'Another send took it over. Check again in a minute.' };
    case 'failed': return { tone: 'bad', text: 'It failed again. The reason is in the list.' };
    case 'gave-up': return { tone: 'bad', text: 'It failed again and was given up. The reason is in the list.' };
    case 'missing': return { tone: 'bad', text: 'That notice no longer exists.' };
    default: return { tone: 'bad', text: 'The server gave an answer this screen does not know.' };
  }
}

/** The status line after a Retry: names the ticket, so it still means something once the row is gone. */
export function resultLine(ticketNo, result) {
  return `${ticketNo}: ${describeRetry(result).text}`;
}

const isWalkthroughBuild = () => import.meta.env?.VITE_WALKTHROUGH_DEMO === 'true';

/** { state: 'off' | 'ok' | 'error', items } - 'off' means: show nothing.
 *  A walkthrough (demo) build never shows the panel: it would put real recipients'
 *  addresses and subjects on a demo screen, and it cannot press Retry anyway. */
export async function loadNotices({ walkthrough = isWalkthroughBuild() } = {}) {
  if (walkthrough) return { state: 'off', items: [] };
  let res;
  try {
    res = await guardedFetch('/api/outbox/attention');
  } catch (err) {
    return { state: 'error', items: [] };
  }
  if (res.status === 404 || res.status === 403 || res.status === 401) return { state: 'off', items: [] };
  if (!res.ok) return { state: 'error', items: [] };
  const body = await res.json().catch(() => null);
  return { state: 'ok', items: Array.isArray(body?.items) ? body.items : [] };
}

/** Press Retry. Throws an Error with plain words when the server refuses. */
export async function retryNotice(eventId) {
  const res = await guardedFetch(`/api/outbox/${encodeURIComponent(eventId)}/retry`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...CSRF },
    body: '{}',
  });
  if (res.status === 403) throw new Error('You do not handle this ticket desk, so you cannot retry it.');
  if (res.status === 404) throw new Error('This notice is not available any more.');
  if (!res.ok) throw new Error('The retry did not go through. Try again in a minute.');
  const body = await res.json().catch(() => ({}));
  return body.result;
}
